// Core
import React, { useEffect, useState } from 'react';

// Libraries
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Plugins
import { DASHBOARD_NULLISH_TEXT, formatNumber } from '../../plugins/general';

// Hooks
import { useOverflowTooltip } from '../../hooks/useOverflowTooltip/useOverflowTooltip';

// Types
import { Theme } from '../../types/theme';

// Components
import { Information } from '../../layout/icons';
import { Paragraph } from '../../layout/elements/Paragraph';
import { Title, TitleSize } from '../../layout/elements/Title';
import { Tooltip, TooltipPlacement } from '../../layout/elements/Tooltip';

// Component
import {
  Container,
  ContentContainer,
  DescriptionContainer,
  DescriptionContent,
  IconContainer,
  InfoContainer,
  InformationContainer,
  TitleContainer,
} from './DashboardIndicator.style';
import { DashboardIndicatorProps } from './DashboardIndicator.types';

function DashboardIndicator({
  className,
  critical = false,
  icon,
  testId,
  title,
  tooltipText,
  value = null,
}: DashboardIndicatorProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme: Theme = useTheme();

  const [formattedValue, setFormattedValue] = useState<string>(
    typeof value === 'string' ? value : formatNumber(value),
  );

  const {
    containerRef,
    contentRef,
    isTooltipVisible,
  } = useOverflowTooltip({
    overflowCheckTriggers: title,
  });

  useEffect((): void => {
    if (value === null) {
      setFormattedValue(DASHBOARD_NULLISH_TEXT);
    } else if (typeof value === 'string') {
      // If value is already a string (e.g., "X/Y" format), use it directly
      setFormattedValue(value);
    } else {
      const minValue: number = 0;
      const maxValue: number = 999999999999;
      const limitedValue: number = Math.min(Math.max(value, minValue), maxValue);
      setFormattedValue(formatNumber(limitedValue));
    }
  }, [value]);

  useEffect((): void => {
    // Force update component when language changes
  }, [i18n.language]);

  return (
    <Container data-testid={testId} className={className}>
      <DescriptionContainer>
        <DescriptionContent critical={critical}>
          <IconContainer>
            {icon}
          </IconContainer>
          <TitleContainer ref={containerRef}>
            {
              isTooltipVisible
                ? (
                  <Tooltip text={title} placement={TooltipPlacement.top}>
                    <ContentContainer ref={contentRef}>
                      <Paragraph
                        color={
                          critical
                            ? theme.color.dashboardIndicator.activeTitleTextColor
                            : theme.color.dashboardIndicator.titleTextColor
                        }
                        preventOverflow
                      >
                        {title}
                      </Paragraph>
                    </ContentContainer>
                  </Tooltip>
                ) : (
                  <ContentContainer ref={contentRef}>
                    <Paragraph
                      color={
                        critical
                          ? theme.color.dashboardIndicator.activeTitleTextColor
                          : theme.color.dashboardIndicator.titleTextColor
                      }
                      preventOverflow
                    >
                      {title}
                    </Paragraph>
                  </ContentContainer>
                )
            }
          </TitleContainer>
        </DescriptionContent>
        <InfoContainer>
          <Tooltip text={tooltipText} placement={TooltipPlacement.left}>
            <InformationContainer>
              <Information />
            </InformationContainer>
          </Tooltip>
        </InfoContainer>
      </DescriptionContainer>
      <Title
        size={TitleSize.xs}
        color={
          critical
            ? theme.color.dashboardIndicator.activeValueTextColor
            : theme.color.dashboardIndicator.valueTextColor
        }
      >
        {formattedValue}
      </Title>
    </Container>
  );
}

export { DashboardIndicator };
